// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Lustre — turning what the player tapped into the exact packed int the rules accept.
//
// The rules are the only authority on what is legal, so this never CONSTRUCTS an action and hopes:
// it enumerates the legal set and finds the one that matches the selection. A screen that builds a
// move the rules would refuse is a screen with a dead button and no explanation, and a screen that
// builds a move the rules accept but the player did not choose is worse.
//
// Patron riders are taken automatically. In Open Wheel and Dry Lode a Patron costs nothing — it
// rides on the action that qualified you — so declining one is never right, and making the player
// tap it would only be a tax on knowing the rules. The action label says it is happening.

import {
  aArg, aPatron, aVerb, actingSeat, gMask, gRep, gTake2, gTake2Hue, legalActions, panHue, cFromShelf,
  cSlot, cTier, sSlot, sTier, NO_PATRON, V_CLAIM, V_CUT, V_GATHER, V_PAN, V_PATRON, V_SHELVE,
  type State,
} from './rules';
import type { Sel } from './view';

/** What a gather action actually draws, as counts per hue. */
export function gatherDraw(arg: number): number[] {
  const out = [0, 0, 0, 0];
  if (gTake2(arg)) {
    out[gTake2Hue(arg)] = 2;
    return out;
  }
  const mask = gMask(arg);
  for (let h = 0; h < 4; h++) if ((mask >> h) & 1) out[h]++;
  for (let i = 0; i < 2; i++) {
    const h = gRep(arg, i);
    if (h >= 0) out[h]++;
  }
  return out;
}

const sameCounts = (a: number[], b: number[]): boolean => a.every((v, i) => v === b[i]);

const countsOf = (hues: number[]): number[] => {
  const out = [0, 0, 0, 0];
  for (const h of hues) out[h]++;
  return out;
};

/**
 * The packed action this selection means, or -1 if it does not (yet) mean anything legal.
 *
 * Where several legal actions match — the same base verb with different Patron riders — the one
 * carrying the most valuable rider wins, because the rider is free.
 */
export function resolveSel(s: State, mySeat: number, sel: Sel): number {
  if (!sel || s.over || actingSeat(s) !== mySeat) return -1;
  const legal = legalActions(s);
  const matches = legal.filter((a) => matchesSel(s, a, sel));
  if (matches.length === 0) return -1;
  return matches.reduce((best, a) => (riderValue(s, a) > riderValue(s, best) ? a : best), matches[0]);
}

function riderValue(s: State, a: number): number {
  const idx = aPatron(a);
  if (idx === NO_PATRON) return 0;
  const id = s.patronsUp[idx];
  return id >= 0 ? s.patronDefs[id].value : 0;
}

function matchesSel(_s: State, a: number, sel: NonNullable<Sel>): boolean {
  const arg = aArg(a);
  switch (sel.kind) {
    case 'gather':
      return aVerb(a) === V_GATHER && sameCounts(gatherDraw(arg), countsOf(sel.hues));
    case 'claim':
      return (
        aVerb(a) === V_CLAIM &&
        cFromShelf(arg) === sel.shelf &&
        cSlot(arg) === sel.slot &&
        (sel.shelf || cTier(arg) === sel.tier)
      );
    case 'shelve':
      return aVerb(a) === V_SHELVE && sTier(arg) === sel.tier && sSlot(arg) === sel.slot;
    case 'cut':
      return aVerb(a) === V_CUT && arg === sel.idx;
    case 'patron':
      return aVerb(a) === V_PATRON && arg === sel.idx;
    case 'pan': {
      if (aVerb(a) !== V_PAN) return false;
      const got: number[] = [];
      for (let i = 0; i < 2; i++) {
        const h = panHue(arg, i);
        if (h >= 0) got.push(h);
      }
      return sameCounts(countsOf(got), countsOf(sel.hues));
    }
    default:
      return false;
  }
}

/**
 * Why the selection cannot be committed, in the words a player would use.
 *
 * This is the only place the rules are ever explained mid-game, so it names the actual obstacle
 * rather than saying the move is illegal — "your bench is full" is a decision, "illegal move" is a
 * bug report.
 */
export function refusalFor(s: State, mySeat: number, sel: Sel): string {
  if (s.over) return '';
  if (actingSeat(s) !== mySeat) return '';
  if (!sel) return '';
  const me = s.players[mySeat];
  const room = s.close - me.pawn;
  switch (sel.kind) {
    case 'gather': {
      const want = countsOf(sel.hues);
      if (want.some((v, h) => v > s.pool[h])) return 'There is not that much left in the pool.';
      const total = sel.hues.length;
      const legal = legalActions(s).filter((a) => aVerb(a) === V_GATHER);
      const sizes = new Set(legal.map((a) => gatherDraw(aArg(a)).reduce((x, y) => x + y, 0)));
      if (sizes.size > 0 && !sizes.has(total)) {
        return `Take ${[...sizes].sort((a, b) => a - b).join(' or ')} — one from each of three piles, plus what your bite earns.`;
      }
      return 'Two of a hue needs five or more in that pile.';
    }
    case 'claim': {
      if (room < 2) return 'Not enough of the wheel left for that.';
      if (!sel.shelf && s.players[mySeat].bench.filter((sp) => !sp.cut && !sp.spent).length >= s.mode.roughCap) {
        return 'Your bench is full — cut something to free a slot.';
      }
      return 'You cannot pay for that yet.';
    }
    case 'shelve':
      return me.shelf.length >= 2 ? 'Your shelf is full.' : 'Not enough of the wheel left for that.';
    case 'cut':
      return room < 1 ? 'Not enough of the wheel left.' : `Cutting costs ${room} more than you have left.`;
    case 'patron':
      return 'You do not have the pips that order asks for.';
    case 'pan':
      return sel.hues.length === 0 ? 'Pick which grit to pan.' : 'The tailings do not hold that.';
    default:
      return '';
  }
}
